import React, { useMemo, useState } from 'react';
import { guessColumnId, parseCsv } from './parseCsv';

export default function CrmCsvImport({ lists = [], selectedLists = [], columns = [], onImport, onClose }) {
  const [listName, setListName] = useState(selectedLists[0] || lists[0] || '');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [records, setRecords] = useState([]);
  const [mapping, setMapping] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const listOptions = useMemo(() => lists, [lists]);

  const handleFile = (file) => {
    if (!file) return;
    setError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result || ''));
      if (!parsed.headers.length) {
        setHeaders([]);
        setRecords([]);
        setMapping({});
        setError('Could not read any columns from that file.');
        return;
      }
      const nextMap = {};
      parsed.headers.forEach((h) => {
        nextMap[h] = guessColumnId(h, columns);
      });
      setHeaders(parsed.headers);
      setRecords(parsed.records);
      setMapping(nextMap);
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!records.length) {
      setError('No member rows found in that CSV.');
      return;
    }
    const rows = records.map((rec) => {
      const data = {};
      headers.forEach((h) => {
        const dest = mapping[h];
        if (!dest || dest === '__skip__') return;
        const value = rec[h];
        if (value === undefined || String(value).trim() === '') return;
        if (dest === '__new__') data[h] = value;
        else {
          const col = columns.find((c) => c.id === dest);
          data[col ? col.name : h] = value;
        }
      });
      return data;
    }).filter((row) => Object.keys(row).length > 0);

    if (!rows.length) {
      setError('Nothing to import. Map at least one column that has values.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      await onImport(listName, rows);
    } catch (e) {
      setError(e.message || 'Import failed');
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Import CSV</div>
        <div className="modal-sub">Rows go into the one CRM. Pick a list to tag them with. Extra CSV columns become extra fields.</div>

        <label className="mlabel">List</label>
        {listOptions.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>Create a list first, then import.</div>
        ) : (
          <select className="mselect" style={{ width: '100%', marginBottom: 14 }} value={listName} onChange={(e) => setListName(e.target.value)}>
            {listOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}

        <label className="mlabel">CSV file</label>
        <label className="csv-file-btn">
          {fileName || 'Choose file'}
          <input type="file" accept=".csv,text/csv" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
        </label>

        {records.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--muted2)', margin: '10px 0 14px' }}>
            {records.length} row{records.length === 1 ? '' : 's'} found
          </div>
        )}

        {headers.length > 0 && (
          <div className="csv-map">
            <div className="csv-map-head">
              <span>CSV column</span>
              <span>Maps to</span>
            </div>
            {headers.map((h) => (
              <div key={h} className="csv-map-row">
                <span className="csv-map-src">{h}</span>
                <select
                  className="mselect"
                  value={mapping[h] || '__new__'}
                  onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}
                >
                  <option value="__new__">New field ({h})</option>
                  <option value="__skip__">Skip</option>
                  {columns.map((col) => (
                    <option key={col.id} value={col.id}>{col.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {error && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 12 }}>{error}</div>}

        <div className="mactions">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-solid" onClick={handleImport} disabled={busy || !listOptions.length || !records.length}>
            {busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}
